/** @jsxImportSource @emotion/react */
import { Typography } from "@mui/material"
import BigNumber from "bignumber.js"
import { LayeredValues, Table, TableColumn, TableProps, TokenIconWithSymbol } from "components"
import React, { FC, useContext, useEffect, useMemo } from "react"
import { useTranslation } from "translation"
import { Market } from "types"

import { STABLECOINS } from "../../../constants"
import { useStyles as useSharedStyles } from "../styles"
import { useStyles as useLocalStyles } from "./styles"
import { AuthContext } from "context/AuthContext"
import {
  formatCentsToReadableValue,
  formatTokensToReadableValue,
  formatToReadablePercentage,
} from "utilities/formatting"
import { unsafelyGetToken } from "utilities/misc"
import useGetMarkets from "queries/getMarkets/useGetMarkets"

export interface MarketTableProps extends Pick<TableProps, "getRowHref"> {
  markets: Market[]
  isLoading: boolean
}

export const MarketTableUi: FC<MarketTableProps> = ({ markets, getRowHref, isLoading }) => {
  const { t } = useTranslation()
  const sharedStyles = useSharedStyles()
  const localStyles = useLocalStyles()

  const columns = useMemo(
    () =>
      [
        { key: "asset", label: t("market.columns.asset"), orderable: false, align: "left" },
        {
          key: "totalSupply",
          label: t("market.columns.totalSupply"),
          orderable: true,
          align: "right",
        },
        { key: "supplyApy", label: t("market.columns.supplyApy"), orderable: true, align: "right" },
        {
          key: "totalBorrows",
          label: t("market.columns.totalBorrow"),
          orderable: true,
          align: "right",
        },
        { key: "borrowApy", label: t("market.columns.borrowApy"), orderable: true, align: "right" },
        {
          key: "utilizationRate",
          label: "Utilization Rate",
          orderable: true,
          align: "right",
        },
        {
          key: "collateralFactor",
          label: t("market.columns.collateralFactor"),
          orderable: true,
          align: "right",
        },
        { key: "price", label: t("market.columns.price"), orderable: true, align: "right" },
      ] as TableColumn[],
    [],
  )

  const cardColumns = useMemo(() => {
    const newColumns = [...columns]
    const [liquidityCol] = newColumns.splice(5, 1)
    newColumns.splice(3, 0, liquidityCol)
    return newColumns
  }, [columns])

  // Format markets to rows
  const rows: TableProps["data"] = useMemo(
    () =>
      markets.map((market) => [
        {
          key: "asset",
          render: () => <TokenIconWithSymbol token={unsafelyGetToken(market.id)} css={localStyles.whiteText} />,
          value: market.id,
        },
        {
          key: "totalSupply",
          render: () => (
            <LayeredValues
              topValue={formatCentsToReadableValue({
                value: market.totalSupplyUsd * 100,
                shortenLargeValue: true,
                showDust: false,
              })}
              bottomValue={formatTokensToReadableValue({
                value: new BigNumber(parseFloat(market.totalSupply2) * market.exchangeRateHumanReadable),
                token: unsafelyGetToken(market.id),
                minimizeDecimals: true,
                shortenLargeValue: true,
              })}
              css={localStyles.noWrap}
            />
          ),
          align: "right",
          value: market.totalSupplyUsd,
        },
        {
          key: "supplyApy",
          render: () =>
            market.id !== "ousg" ? (
              <Typography variant="body1">{formatToReadablePercentage(market.supplyApy)}</Typography>
            ) : (
              <Typography variant="body2">N/A</Typography>
            ),
          value: market.supplyApy.toNumber(),
          align: "right",
        },
        {
          key: "totalBorrows",
          render: () =>
            market.id !== "ousg" ? (
              <LayeredValues
                topValue={formatCentsToReadableValue({
                  value: market.totalBorrowsUsd * 100,
                  shortenLargeValue: true,
                  showDust: false,
                })}
                bottomValue={formatTokensToReadableValue({
                  value: new BigNumber(market.totalBorrows2),
                  token: unsafelyGetToken(market.id),
                  minimizeDecimals: true,
                  shortenLargeValue: true,
                })}
                css={localStyles.noWrap}
              />
            ) : (
              <Typography variant="body2">N/A</Typography>
            ),
          value: market.totalBorrows2,
          align: "right",
        },
        {
          key: "borrowApy",
          render: () =>
            market.id !== "ousg" ? (
              <Typography variant="body1">{formatToReadablePercentage(market.borrowApy)}</Typography>
            ) : (
              <Typography variant="body2">N/A</Typography>
            ),
          value: market.borrowApy.toNumber(),
          align: "right",
        },
        {
          key: "utilizationRate",
          render: () =>
            market.id !== "ousg" ? (
              <Typography variant="body1">
                {formatToReadablePercentage((market.totalBorrowsUsd / market.totalSupplyUsd) * 100)}
              </Typography>
            ) : (
              <Typography variant="body2">N/A</Typography>
            ),
          value: market.totalBorrowsUsd / market.totalSupplyUsd,
          align: "right",
        },
        {
          key: "collateralFactor",
          render: () =>
            STABLECOINS.includes(market.id.toUpperCase()) ? (
              <Typography variant="body2">N/A</Typography>
            ) : (
              <Typography variant="body1" css={localStyles.whiteText}>
                {formatToReadablePercentage(market.collateralFactor * 100)}
              </Typography>
            ),
          value: market.collateralFactor,
          align: "right",
        },
        {
          key: "price",
          render: () => (
            <Typography variant="body1" css={localStyles.whiteText}>
              {formatCentsToReadableValue({ value: market.tokenPrice.multipliedBy(100) })}
            </Typography>
          ),
          align: "right",
          value: market.tokenPrice.toNumber(),
        },
      ]),
    [JSON.stringify(markets)],
  )

  return (
    <Table
      columns={columns}
      cardColumns={cardColumns}
      data={rows}
      initialOrder={{
        orderBy: "supplyApy",
        orderDirection: "desc",
      }}
      rowKeyIndex={0}
      getRowHref={getRowHref}
      tableCss={sharedStyles.table}
      cardsCss={sharedStyles.cards}
      css={localStyles.cardContentGrid}
      isFetching={isLoading}
    />
  )
}

const MarketTable = () => {
  const { data: { markets } = { markets: [] }, isLoading, refetch } = useGetMarkets()
  const { chain } = useContext(AuthContext)

  useEffect(() => {
    refetch()
  }, [chain?.id])

  return <MarketTableUi isLoading={isLoading} markets={markets} getRowHref={(row) => `/market/${row[0].value}`} />
}

export default MarketTable
